import { api } from './api';

/**
 * Authentication service for user login, registration, and session management
 */
class AuthService {
  /**
   * Authenticate user with email and password
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @param {string} deviceId - Device identifier for security
   * @returns {Promise<Object>} Authentication result
   */
  async login(email, password, deviceId) {
    if (!email || !password || !deviceId) {
      throw new Error('Email, password, and device ID are required');
    }

    try {
      const response = await api.post('/auth/login', {
        email_id: email,
        password: password,
        device_identifier: deviceId
      });
      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  /**
   * Register a new user
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<Object>} Registration result
   */
  async register(email, password) {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    try {
      const response = await api.post('/auth/register', {
        email,
        password
      });
      return response.data;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  }

  /**
   * Get current authenticated user
   * @returns {Promise<Object>} User data
   */
  async getCurrentUser() {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Failed to get current user:', error);
      throw error;
    }
  }

  /**
   * Logout current user
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Log but don't throw - logout should succeed even if API fails
      console.warn('Logout API call failed:', error);
    }
  }

  /**
   * Refresh access token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<Object>} New tokens
   */
  async refreshToken(refreshToken) {
    if (!refreshToken) {
      throw new Error('Refresh token is required');
    }

    try {
      const response = await api.post('/auth/refresh', {
        refresh_token: refreshToken
      });
      return response.data;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Logout from all devices
   * @returns {Promise<void>}
   */
  async logoutAllDevices() {
    try {
      await api.post('/auth/logout-all-devices');
    } catch (error) {
      console.error('Logout all devices error:', error);
      throw error;
    }
  }

  /**
   * Get active sessions for current user
   * @returns {Promise<Array>} Active sessions
   */
  async getActiveSessions() {
    try {
      const response = await api.get('/auth/sessions');
      return response.data;
    } catch (error) {
      console.error('Failed to get active sessions:', error);
      throw error;
    }
  }
}

export const authService = new AuthService();
export default authService;

